import {
  MdLuggage,
  MdEdit,
  MdDelete,
  MdVisibility,
  MdPerson,
  MdFlight,
  MdBusiness,
  MdScale,
  MdStraighten,
} from 'react-icons/md'
import { Badge } from '../../components/ui'

const BaggageList = ({ baggage, onViewBaggage, onEditBaggage, onDeleteBaggage }) => {
  const formatWeight = (weight) => {
    return `${weight} kg`
  }

  const formatDimensions = (dimensions) => {
    if (!dimensions) return 'N/A'
    return `${dimensions.length} × ${dimensions.width} × ${dimensions.height} ${dimensions.unit}`
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-4">
      {baggage.map((item) => (
        <div
          key={item._id}
          className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 hover:shadow-lg dark:hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Baggage Basic Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                      Baggage #{item?.tagNumber?.slice(4)}
                    </h3>
                    <Badge variant="info">
                      <div className="flex items-center gap-1">
                        <MdLuggage className="text-sm" />
                        <span>Checked</span>
                      </div>
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-600 dark:text-neutral-400">
                    <div className="flex items-center gap-1">
                      <MdPerson className="text-lg" />
                      <span>
                        {item.passenger?.firstName} {item.passenger?.lastName}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MdFlight className="text-lg" />
                      <span>{item.flight?.number}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MdBusiness className="text-lg" />
                      <span>{item.flight?.airline?.name}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Baggage Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Weight & Dimensions
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div className="flex items-center gap-1">
                      <MdScale className="text-sm" />
                      <span>{formatWeight(item.weight)}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <MdStraighten className="text-sm" />
                      <span className="text-neutral-600 dark:text-neutral-400">
                        {formatDimensions(item.dimensions)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Flight Details
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>{item.flight?.number}</div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      {item.flight?.departure ? formatDateTime(item.flight.departure) : 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Passenger Info
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>
                      {item.passenger?.firstName} {item.passenger?.lastName}
                    </div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      {item.passenger?.passport?.number}
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Created
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>{formatDate(item.createdAt)}</div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      {formatDate(item.updatedAt)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-2">
                <button
                  onClick={() => onViewBaggage(item)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors duration-200"
                >
                  <MdVisibility className="text-sm" />
                  View
                </button>
                <button
                  onClick={() => onEditBaggage(item)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-warning-600 dark:text-warning-400 hover:text-warning-700 dark:hover:text-warning-300 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded-lg transition-colors duration-200"
                >
                  <MdEdit className="text-sm" />
                  Edit
                </button>
                <button
                  onClick={() => onDeleteBaggage(item)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors duration-200"
                >
                  <MdDelete className="text-sm" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default BaggageList
